export type InlineSegment = string | { text: string; href: string };

export type BlogBlock =
  | { type: "paragraph"; content: InlineSegment[] }
  | { type: "subheading"; text: string }
  | { type: "list"; ordered?: boolean; items: InlineSegment[][] }
  | { type: "stat"; value: string; description: string }
  | { type: "script"; lines: string[]; signoff?: string };

export interface BlogSection {
  id: string;
  heading: string;
  blocks: BlogBlock[];
}

export interface BlogPost {
  slug: string;
  title: string;
  metaDescription: string;
  introSnippet: string;
  intro: InlineSegment[][];
  tableOfContents: { id: string; label: string }[];
  sections: BlogSection[];
}

export const blogPosts: BlogPost[] = [
  {
    slug: "build-a-team-that-actually-feels-heard",
    title: "How 4 Simple Steps Can Help You Build a Team That Actually Feels Heard",
    metaDescription:
      "Most managers only check in once a quarter — or once a year. Learn 4 simple steps to build a weekly feedback rhythm with TeamPulse so your team actually feels heard.",
    introSnippet:
      "You lead a team. You care about them. But it's easy to lose touch with how people are actually doing. Here are 4 simple steps to build a weekly rhythm that keeps your team feeling heard.",
    intro: [
      [
        "You lead a team. You care about them. But between back-to-back meetings and overflowing inboxes, it's easy to lose touch with how people are actually doing.",
      ],
      [
        "Building a team that actually feels heard doesn't happen by accident. More than half of employees — 58% — want to be asked more often how they're doing. Yet most managers rely on annual reviews or the occasional one-on-one to check in. By the time you notice a problem, the damage is often already done.",
      ],
      [
        "When feedback only flows once a quarter — or once a year — small frustrations pile up in silence. That silence is the gap TeamPulse weekly check-ins are designed to close.",
      ],
      [
        "TeamPulse gives you a simple, consistent way to hear from every person on your team — every single week — before small frustrations become big problems.",
      ],
      ["Ready to see how it works? Here's how to get your team up and running in just 4 steps."],
    ],
    tableOfContents: [
      {
        id: "why-weekly-check-ins-matter",
        label: "Why Weekly Team Check-ins Matter More Than You Think",
      },
      { id: "what-is-teampulse", label: "What Is TeamPulse — and How Does It Work?" },
      { id: "4-steps-to-get-started", label: "4 Steps to Get Your Team Up and Running This Week" },
      {
        id: "makes-or-breaks-feedback-culture",
        label: "The 1 Thing That Makes or Breaks a Feedback Culture",
      },
      { id: "success-30-days-from-now", label: "What Success Looks Like 30 Days From Now" },
    ],
    sections: [
      {
        id: "why-weekly-check-ins-matter",
        heading: "Why Weekly Team Check-ins Matter More Than You Think",
        blocks: [
          {
            type: "paragraph",
            content: [
              "A weekly check-in might seem small, but its impact on your team is bigger than you'd expect.",
            ],
          },
          {
            type: "paragraph",
            content: [
              "Disengagement is expensive. According to a 2024 Gallup study, only 30% of U.S. employees are engaged at work — the lowest level since 2013. And the cost of doing nothing? Gallup estimates low engagement drains nearly a tenth of worldwide GDP annually.",
            ],
          },
          {
            type: "stat",
            value: "30%",
            description:
              "of U.S. employees are engaged at work — the lowest level since 2013 (2024 Gallup study).",
          },
          { type: "paragraph", content: ["The good news?"] },
          {
            type: "paragraph",
            content: [
              "You don't need a big HR department or a complex system to fix it. You just need a better feedback loop.",
            ],
          },
          { type: "paragraph", content: ["Here's why regular check-ins work:"] },
          {
            type: "list",
            items: [
              ["They bring problems to the surface before they become huge issues."],
              ["They give employees a voice, which builds trust."],
              ["They help managers respond personally — not reactively."],
              ["They create a consistent rhythm that teams value."],
            ],
          },
          {
            type: "paragraph",
            content: [
              "Highly engaged teams see 21% less turnover in organizations where attrition is already high, and 51% less in those where it's traditionally low. That's the difference between a thriving team and one that quietly falls apart.",
            ],
          },
          {
            type: "stat",
            value: "51%",
            description:
              "less turnover among highly engaged teams in organizations where attrition is traditionally low.",
          },
        ],
      },
      {
        id: "what-is-teampulse",
        heading: "What Is TeamPulse — and How Does It Work?",
        blocks: [
          {
            type: "paragraph",
            content: [
              "TeamPulse is a weekly check-in tool built for managers who want to stay genuinely connected to their teams. It's simple, personal, and takes only a few minutes for each team member to complete.",
            ],
          },
          {
            type: "paragraph",
            content: ["Each week, team members receive a short check-in where they can share:"],
          },
          {
            type: "list",
            items: [
              ["Their wins from the week"],
              ["Challenges they're facing"],
              ["How they're feeling overall"],
            ],
          },
          {
            type: "paragraph",
            content: [
              "Responses are confidential and go directly to the manager. That personal connection is what makes it different.",
            ],
          },
        ],
      },
      {
        id: "4-steps-to-get-started",
        heading: "4 Steps to Get Your Team Up and Running This Week",
        blocks: [
          {
            type: "paragraph",
            content: [
              "You don't need a long onboarding process. TeamPulse is designed to be simple from day one. Here's exactly what to do in your first week:",
            ],
          },
          { type: "subheading", text: "Step 1: Invite Your Team" },
          {
            type: "paragraph",
            content: [
              "Log in to TeamPulse, head to Team Management, and add your team members by email. They'll automatically receive an invitation to join.",
            ],
          },
          {
            type: "paragraph",
            content: [
              "Before you do, consider sending a quick note to your team to set the stage. Here's a short script you can adapt:",
            ],
          },
          {
            type: "script",
            lines: [
              "Hi team,",
              "I'm excited to introduce a new tool we'll be using called TeamPulse. Each week, you'll receive a short check-in — it only takes a few minutes — where you can share your wins, challenges, and how you're feeling overall.",
              "TeamPulse is a simple way for me to stay connected with each of you, celebrate what's going well, and make sure I can support you when things get tough. Your responses come directly to me, and I'll read and reply to them personally.",
              "You'll get an invitation shortly. All you need to do is accept it, and you'll receive your first check-in soon after. I'm looking forward to hearing from you! Let me know if you have any questions or concerns.",
            ],
            signoff: "— [Your Name]",
          },
          {
            type: "paragraph",
            content: [
              'Being upfront with your team and giving them the "why" before they even receive the invitation helps you avoid confusion and questions. When people understand the "why," they\'re far more likely to participate.',
            ],
          },
          { type: "subheading", text: "Step 2: Review Your Settings" },
          {
            type: "paragraph",
            content: [
              "Before the first check-in goes out, spend a few minutes on the Settings Page. This is where you'll find options for timing and customizations.",
            ],
          },
          {
            type: "paragraph",
            content: [
              "By default, anyone with an invitation will receive their first weekly check-in on Friday at 11:00 AM EST. You can adjust this to fit your team's schedule.",
            ],
          },
          {
            type: "paragraph",
            content: ["This step takes five minutes. Don't skip it."],
          },
          { type: "subheading", text: "Step 3: Read Your Team Reports" },
          {
            type: "paragraph",
            content: [
              "After responses come in, go to Team Reports. You'll see an overview of your team's wellbeing at a glance. Then click each team member to read their full report.",
            ],
          },
          { type: "paragraph", content: ["This is where the real value lives."] },
          {
            type: "paragraph",
            content: [
              "You'll start noticing patterns — who's thriving, who's struggling, and where your team needs support. What you do with this information is what separates good managers from great ones.",
            ],
          },
          { type: "subheading", text: "Step 4: Reply Personally to Every Team Member" },
          { type: "paragraph", content: ["This step is the most important one."] },
          {
            type: "paragraph",
            content: [
              "Once you've read a team member's report, reply to it. It doesn't need to be long. A few thoughtful sentences acknowledging what they shared go a long way.",
            ],
          },
          {
            type: "paragraph",
            content: [
              "The task of replying can be divided between admins and supervisors, so no one gets overwhelmed. But the keyword here is personal. A copy-paste response defeats the purpose.",
            ],
          },
          {
            type: "paragraph",
            content: [
              "According to the Achievers Workforce Institute, 91% of employees who receive meaningful recognition at least once a month say they're highly engaged. Your reply is recognition. Treat it like one.",
            ],
          },
          {
            type: "stat",
            value: "91%",
            description:
              "of employees who receive meaningful recognition at least once a month say they're highly engaged (Achievers Workforce Institute).",
          },
        ],
      },
      {
        id: "makes-or-breaks-feedback-culture",
        heading: "The 1 Thing That Makes or Breaks a Feedback Culture",
        blocks: [
          { type: "paragraph", content: ["You can set up every tool in the world."] },
          { type: "paragraph", content: ["But if managers don't respond, people stop sharing."] },
          {
            type: "paragraph",
            content: [
              "Research shows that when managers don't act on feedback, employees disengage and stop sharing altogether. Your replies aren't just nice — they're the foundation of a culture that retains talent and drives performance.",
            ],
          },
          {
            type: "paragraph",
            content: [
              "So here's a question worth asking yourself: When was the last time a team member felt truly heard at work?",
            ],
          },
          {
            type: "paragraph",
            content: [
              "It's not just about getting people to talk. It's about whether you listen and respond when they do.",
            ],
          },
        ],
      },
      {
        id: "success-30-days-from-now",
        heading: "What Success Looks Like 30 Days From Now",
        blocks: [
          {
            type: "paragraph",
            content: [
              "A month from now, you'll have a clear picture of your team's wellbeing — not a snapshot from an annual review, but a living, week-by-week record of how your people are doing.",
            ],
          },
          {
            type: "paragraph",
            content: [
              "You'll know who's been carrying extra stress. You'll know who had a quiet win they never mentioned in a meeting. You'll know who needs a stretch assignment and who needs a little more support right now.",
            ],
          },
          { type: "paragraph", content: ["And your team? They'll know you're paying attention."] },
          {
            type: "paragraph",
            content: [
              "That shift — from manager to trusted leader — doesn't happen by accident. It happens when you build the habit of consistently asking and listening.",
            ],
          },
          { type: "paragraph", content: ["Here's your action plan:"] },
          {
            type: "list",
            ordered: true,
            items: [
              [
                "Go to ",
                { text: "teampulse.us", href: "https://teampulse.us" },
                " and log in today.",
              ],
              ["Send your team a short note explaining why you're using it."],
              ["Invite your team (it takes less than 5 minutes)."],
              ["Commit to reading and replying to every check-in for the first month."],
            ],
          },
          {
            type: "paragraph",
            content: [
              "That's it. In four simple steps, you'll be well on your way to leading a team that actually feels heard.",
            ],
          },
          {
            type: "paragraph",
            content: [
              "Questions? Visit ",
              { text: "teampulse.us/contact", href: "https://teampulse.us/contact" },
            ],
          },
        ],
      },
    ],
  },
];

export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPosts.find((post) => post.slug === slug);
}
